'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Booking {
  id: number;
  property: string;
  location: string;
  image: string;
  imageAlt: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  total: string;
  status: 'active' | 'upcoming' | 'completed' | 'cancelled';
  bookingRef: string;
  host: string;
  rating?: number;
}

interface WishlistItem {
  id: number;
  property: string;
  location: string;
  image: string;
  imageAlt: string;
  price: string;
  rating: number;
  reviews: number;
  type: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const bookings: Booking[] = [
  {
    id: 1,
    property: 'Luxury Villa Kololo',
    location: 'Kampala, Uganda',
    image: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=500&h=320&fit=crop',
    imageAlt: 'Luxury villa with swimming pool in Kololo Kampala',
    checkIn: 'Aug 25, 2026',
    checkOut: 'Aug 30, 2026',
    nights: 5,
    total: 'UGX 2,500,000',
    status: 'active',
    bookingRef: 'NDT-2026-0821',
    host: 'James Ssekandi',
  },
  {
    id: 2,
    property: 'Lakeside Retreat Entebbe',
    location: 'Entebbe, Uganda',
    image: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=500&h=320&fit=crop',
    imageAlt: 'Lakeside retreat with views of Lake Victoria in Entebbe',
    checkIn: 'Sep 12, 2026',
    checkOut: 'Sep 15, 2026',
    nights: 3,
    total: 'UGX 1,200,000',
    status: 'upcoming',
    bookingRef: 'NDT-2026-0834',
    host: 'Grace Achieng',
  },
  {
    id: 3,
    property: 'Safari Lodge Murchison',
    location: 'Murchison Falls, Uganda',
    image: 'https://images.unsplash.com/photo-1493246507139-91e8fad9978e?w=500&h=320&fit=crop',
    imageAlt: 'Safari lodge surrounded by savanna at Murchison Falls National Park',
    checkIn: 'Jul 10, 2026',
    checkOut: 'Jul 14, 2026',
    nights: 4,
    total: 'UGX 3,200,000',
    status: 'completed',
    bookingRef: 'NDT-2026-0712',
    host: 'David Ochieng',
    rating: 5,
  },
  {
    id: 4,
    property: 'Mountain Cabin Rwenzori',
    location: 'Kasese, Uganda',
    image: 'https://images.unsplash.com/photo-1542718610-a1d656d1884c?w=500&h=320&fit=crop',
    imageAlt: 'Cozy mountain cabin with Rwenzori mountain views in Kasese',
    checkIn: 'May 20, 2026',
    checkOut: 'May 23, 2026',
    nights: 3,
    total: 'UGX 900,000',
    status: 'completed',
    bookingRef: 'NDT-2026-0520',
    host: 'Ruth Kemigisha',
    rating: 4,
  },
  {
    id: 5,
    property: 'City Apartment Kampala',
    location: 'Kampala, Uganda',
    image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=500&h=320&fit=crop',
    imageAlt: 'Modern city apartment in central Kampala Uganda',
    checkIn: 'Apr 2, 2026',
    checkOut: 'Apr 4, 2026',
    nights: 2,
    total: 'UGX 450,000',
    status: 'cancelled',
    bookingRef: 'NDT-2026-0402',
    host: 'Peter Mugisha',
  },
];

const wishlistItems: WishlistItem[] = [
  {
    id: 1,
    property: 'Gorilla Trekking Lodge',
    location: 'Bwindi, Uganda',
    image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=400&h=280&fit=crop',
    imageAlt: 'Luxury lodge nestled in Bwindi Impenetrable Forest near gorilla habitat',
    price: 'UGX 850,000/night',
    rating: 4.9,
    reviews: 124,
    type: 'Lodge',
  },
  {
    id: 2,
    property: 'Nile River Houseboat',
    location: 'Jinja, Uganda',
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=280&fit=crop',
    imageAlt: 'Houseboat floating on the Nile River near Jinja source',
    price: 'UGX 600,000/night',
    rating: 4.7,
    reviews: 89,
    type: 'Houseboat',
  },
  {
    id: 3,
    property: 'Kigali Hilltop Villa',
    location: 'Kigali, Rwanda',
    image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=400&h=280&fit=crop',
    imageAlt: 'Modern hilltop villa overlooking Kigali city skyline Rwanda',
    price: 'UGX 1,200,000/night',
    rating: 4.8,
    reviews: 56,
    type: 'Villa',
  },
  {
    id: 4,
    property: 'Zanzibar Beach Cottage',
    location: 'Zanzibar, Tanzania',
    image: 'https://images.unsplash.com/photo-1439130490301-25e322d88054?w=400&h=280&fit=crop',
    imageAlt: 'White beach cottage with turquoise ocean views in Zanzibar',
    price: 'UGX 950,000/night',
    rating: 4.9,
    reviews: 203,
    type: 'Cottage',
  },
];

// ─── Status config ─────────────────────────────────────────────────────────────

const statusConfig: Record<string, { label: string; color: string; dot: string }> = {
  active: { label: 'Active Stay', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  upcoming: { label: 'Upcoming', color: 'bg-sky-100 text-sky-700', dot: 'bg-sky-500' },
  completed: { label: 'Completed', color: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-600', dot: 'bg-red-400' },
};

// ─── Sub-sections ──────────────────────────────────────────────────────────────

function ActiveReservations({ bookings }: { bookings: Booking[] }) {
  const active = bookings.filter((b) => b.status === 'active' || b.status === 'upcoming');
  return (
    <section>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
          Active Reservations
        </h2>
        <span className="text-xs font-semibold bg-rose-100 text-rose-600 px-3 py-1 rounded-full">
          {active.length} active
        </span>
      </div>
      {active.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <div className="text-4xl mb-3">🏡</div>
          <p className="text-gray-500 text-sm">No active reservations right now</p>
          <a href="/search" className="inline-block mt-4 px-5 py-2 bg-[#4088C6] text-white text-sm font-semibold rounded-full hover:bg-[#2D6FA8] transition-colors">
            Find a Stay
          </a>
        </div>
      ) : (
        <div className="space-y-4">
          {active.map((booking) => (
            <div key={booking.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
              <div className="flex flex-col sm:flex-row">
                <div className="relative sm:w-52 h-44 sm:h-auto flex-shrink-0">
                  <img src={booking.image} alt={booking.imageAlt} className="w-full h-full object-cover" />
                  <div className="absolute top-3 left-3">
                    <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${statusConfig[booking.status].color}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${statusConfig[booking.status].dot} ${booking.status === 'active' ? 'animate-pulse' : ''}`} />
                      {statusConfig[booking.status].label}
                    </span>
                  </div>
                </div>
                <div className="flex-1 p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="font-bold text-gray-900 text-base">{booking.property}</h3>
                      <span className="text-sm font-bold text-gray-900 ml-4">{booking.total}</span>
                    </div>
                    <p className="text-sm text-gray-500 mb-3">📍 {booking.location} · Host: {booking.host}</p>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                      <span className="bg-gray-50 px-3 py-1.5 rounded-lg">📅 {booking.checkIn} → {booking.checkOut}</span>
                      <span className="bg-gray-50 px-3 py-1.5 rounded-lg">🌙 {booking.nights} nights</span>
                      <span className="bg-gray-50 px-3 py-1.5 rounded-lg font-mono">#{booking.bookingRef}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button className="px-4 py-2 text-xs font-semibold text-[#4088C6] border border-[#4088C6]/30 rounded-xl hover:bg-[#4088C6]/10 transition-colors">
                      View Details
                    </button>
                    <a href="/messages" className="px-4 py-2 text-xs font-semibold text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                      Message Host
                    </a>
                    {booking.status === 'upcoming' && (
                      <button className="px-4 py-2 text-xs font-semibold text-red-500 border border-red-100 rounded-xl hover:bg-red-50 transition-colors ml-auto">
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function PastBookings({ bookings }: { bookings: Booking[] }) {
  const [filter, setFilter] = useState<'all' | 'completed' | 'cancelled'>('all');
  const past = bookings.filter((b) => b.status === 'completed' || b.status === 'cancelled');
  const filtered = past.filter((b) => filter === 'all' || b.status === filter);

  return (
    <section>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
          Past Bookings
        </h2>
        <div className="flex gap-2">
          {(['all', 'completed', 'cancelled'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition-all ${
                filter === f ? 'bg-[#4088C6] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#4088C6]/40'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { label: 'Total Trips', value: past.length.toString(), icon: '✈️' },
          { label: 'Nights Stayed', value: past.filter(b => b.status === 'completed').reduce((a, b) => a + b.nights, 0).toString(), icon: '🌙' },
          { label: 'Countries', value: '3', icon: '🌍' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm text-center">
            <div className="text-xl mb-1">{s.icon}</div>
            <div className="text-2xl font-bold text-gray-900">{s.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map((booking) => (
          <div key={booking.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
            <div className="flex items-center gap-4 p-4">
              <div className="w-20 h-16 rounded-xl overflow-hidden flex-shrink-0">
                <img src={booking.image} alt={booking.imageAlt} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm truncate">{booking.property}</h3>
                    <p className="text-xs text-gray-500">{booking.location}</p>
                  </div>
                  <span className={`flex-shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${statusConfig[booking.status].color}`}>
                    {statusConfig[booking.status].label}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                  <span>📅 {booking.checkIn} – {booking.checkOut}</span>
                  <span className="hidden sm:inline">·</span>
                  <span className="hidden sm:inline font-bold text-gray-900">{booking.total}</span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                {booking.rating && (
                  <div className="flex items-center gap-1 bg-amber-50 px-2 py-1 rounded-lg">
                    <span className="text-amber-500 text-xs">★</span>
                    <span className="text-xs font-bold text-amber-700">{booking.rating}</span>
                  </div>
                )}
                {booking.status === 'completed' && !booking.rating && (
                  <button className="text-xs font-semibold text-white bg-[#4088C6] px-3 py-1.5 rounded-xl hover:bg-[#2D6FA8] transition-colors">
                    Review
                  </button>
                )}
                {booking.status === 'completed' && booking.rating && (
                  <button className="text-xs font-semibold text-gray-600 border border-gray-200 px-3 py-1.5 rounded-xl hover:bg-gray-50 transition-colors">
                    Book Again
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-10 text-gray-400 text-sm">No bookings in this category</div>
        )}
      </div>
    </section>
  );
}

function SavedWishlist() {
  const [saved, setSaved] = useState<number[]>(wishlistItems.map((i) => i.id));

  const toggleSave = (id: number) => {
    setSaved((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
          Saved Wishlist
        </h2>
        <span className="text-xs font-semibold text-gray-500">{saved.length} saved</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {wishlistItems.map((item) => (
          <div key={item.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow group">
            <div className="relative h-44">
              <img src={item.image} alt={item.imageAlt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              <button
                onClick={() => toggleSave(item.id)}
                className="absolute top-3 right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md hover:scale-110 transition-transform"
              >
                <svg className={`w-4 h-4 ${saved.includes(item.id) ? 'text-rose-500 fill-rose-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </button>
              <div className="absolute bottom-3 left-3">
                <span className="text-xs font-semibold bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-full text-gray-700">{item.type}</span>
              </div>
            </div>
            <div className="p-4">
              <h3 className="font-bold text-gray-900 text-sm mb-0.5">{item.property}</h3>
              <p className="text-xs text-gray-500 mb-2">📍 {item.location}</p>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1">
                  <span className="text-amber-400 text-xs">★</span>
                  <span className="text-xs font-semibold text-gray-700">{item.rating}</span>
                  <span className="text-xs text-gray-400">({item.reviews})</span>
                </div>
                <span className="text-xs font-bold text-gray-900">{item.price}</span>
              </div>
              <a href="/search" className="block w-full py-2 text-center text-xs font-semibold text-[#4088C6] border border-[#4088C6]/30 rounded-xl hover:bg-[#4088C6]/10 transition-colors">
                View Property
              </a>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function AccountSettings() {
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [notifications, setNotifications] = useState({
    bookingConfirmations: true,
    hostMessages: true,
    promotionalOffers: false,
    reviewReminders: true,
    priceAlerts: true,
  });
  const [form, setForm] = useState({
    firstName: 'Sarah',
    lastName: 'Nakato',
    email: 'sarah.nakato@email.com',
    phone: '+256 700 123 456',
    country: 'Uganda',
    city: 'Kampala',
    bio: 'Passionate traveler exploring East Africa one stay at a time. Love nature, wildlife, and authentic local experiences.',
    language: 'English',
    currency: 'UGX',
  });

  const handleSave = () => {
    setEditing(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const toggleNotif = (key: keyof typeof notifications) => {
    setNotifications((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <section className="space-y-6">
      <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
        Account Settings
      </h2>

      {saved && (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-2xl text-sm font-semibold">
          <span>✓</span> Profile updated successfully
        </div>
      )}

      {/* Profile card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center gap-5 mb-6">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center text-white font-bold text-3xl shadow-lg">
              S
            </div>
            <button className="absolute bottom-0 right-0 w-7 h-7 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors">
              <svg className="w-3.5 h-3.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900">{form.firstName} {form.lastName}</h3>
            <p className="text-sm text-gray-500">{form.city}, {form.country} · Member since 2022</p>
            <div className="flex items-center gap-3 mt-1.5">
              <span className="flex items-center gap-1 text-xs text-amber-600 font-semibold">
                <span>★</span> 4.9 guest rating
              </span>
              <span className="text-gray-300">·</span>
              <span className="text-xs text-gray-500">6 trips completed</span>
            </div>
          </div>
          <button
            onClick={() => editing ? handleSave() : setEditing(true)}
            className={`px-4 py-2 text-sm font-semibold rounded-xl transition-all ${
              editing ? 'bg-[#4088C6] text-white hover:bg-[#2D6FA8]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {editing ? 'Save Changes' : 'Edit Profile'}
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {([
            { label: 'First Name', field: 'firstName' },
            { label: 'Last Name', field: 'lastName' },
            { label: 'Email Address', field: 'email' },
            { label: 'Phone Number', field: 'phone' },
            { label: 'Country', field: 'country' },
            { label: 'City', field: 'city' },
          ] as { label: string; field: keyof typeof form }[]).map(({ label, field }) => (
            <div key={field}>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">{label}</label>
              {editing ? (
                <input
                  type="text"
                  value={form[field]}
                  onChange={(e) => setForm((p) => ({ ...p, [field]: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent"
                />
              ) : (
                <p className="text-sm text-gray-900 py-2.5 px-4 bg-gray-50 rounded-xl">{form[field]}</p>
              )}
            </div>
          ))}
        </div>
        <div className="mt-4">
          <label className="block text-xs font-semibold text-gray-500 mb-1.5">Bio</label>
          {editing ? (
            <textarea
              value={form.bio}
              onChange={(e) => setForm((p) => ({ ...p, bio: e.target.value }))}
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-transparent resize-none"
            />
          ) : (
            <p className="text-sm text-gray-700 py-2.5 px-4 bg-gray-50 rounded-xl leading-relaxed">{form.bio}</p>
          )}
        </div>
      </div>

      {/* Preferences */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <h3 className="text-base font-bold text-gray-900 mb-5">Preferences</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Language</label>
            <select className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-300 bg-white">
              <option>English</option>
              <option>Swahili</option>
              <option>French</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Currency</label>
            <select className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-rose-300 bg-white">
              <option>UGX — Ugandan Shilling</option>
              <option>USD — US Dollar</option>
              <option>KES — Kenyan Shilling</option>
            </select>
          </div>
        </div>

        {/* Notification toggles */}
        <div className="border-t border-gray-100 pt-5">
          <h4 className="text-sm font-bold text-gray-700 mb-4">Notification Preferences</h4>
          <div className="space-y-1">
            {(Object.entries(notifications) as [keyof typeof notifications, boolean][]).map(([key, val]) => {
              const labels: Record<keyof typeof notifications, string> = {
                bookingConfirmations: 'Booking confirmations',
                hostMessages: 'Host messages',
                promotionalOffers: 'Promotional offers',
                reviewReminders: 'Review reminders',
                priceAlerts: 'Price drop alerts',
              };
              return (
                <label key={key} className="flex items-center justify-between py-2.5 cursor-pointer group">
                  <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">{labels[key]}</span>
                  <button
                    onClick={() => toggleNotif(key)}
                    className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${val ? 'bg-[#4088C6]' : 'bg-gray-200'}`}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${val ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {/* Security */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <h3 className="text-base font-bold text-gray-900 mb-5">Security</h3>
        <div className="space-y-3">
          {[
            { label: 'Change Password', desc: 'Last changed 3 months ago', icon: '🔑' },
            { label: 'Two-Factor Authentication', desc: 'Not enabled — add extra security', icon: '🛡️' },
            { label: 'Connected Accounts', desc: 'Google · Facebook', icon: '🔗' },
          ].map((item) => (
            <div key={item.label} className="flex items-center justify-between p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer">
              <div className="flex items-center gap-3">
                <span className="text-xl">{item.icon}</span>
                <div>
                  <p className="text-sm font-semibold text-gray-900">{item.label}</p>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </div>
              </div>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          ))}
        </div>
      </div>

      {/* Danger zone */}
      <div className="bg-white rounded-2xl border border-red-100 shadow-sm p-6">
        <h3 className="text-base font-bold text-red-600 mb-4">Danger Zone</h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">Delete Account</p>
            <p className="text-xs text-gray-500 mt-0.5">Permanently remove your account and all data</p>
          </div>
          <button className="px-4 py-2 text-sm font-semibold text-red-600 border border-red-200 rounded-xl hover:bg-red-50 transition-colors">
            Delete Account
          </button>
        </div>
      </div>
    </section>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

const sections = [
  { id: 'reservations', label: 'Reservations', icon: '🏡' },
  { id: 'past', label: 'Past Bookings', icon: '📋' },
  { id: 'wishlist', label: 'Wishlist', icon: '❤️' },
  { id: 'settings', label: 'Account Settings', icon: '⚙️' },
];

export default function GuestProfilePage() {
  const [activeSection, setActiveSection] = useState('reservations');

  const renderSection = () => {
    switch (activeSection) {
      case 'reservations': return <ActiveReservations bookings={bookings} />;
      case 'past': return <PastBookings bookings={bookings} />;
      case 'wishlist': return <SavedWishlist />;
      case 'settings': return <AccountSettings />;
      default: return <ActiveReservations bookings={bookings} />;
    }
  };

  const activeCount = bookings.filter((b) => b.status === 'active' || b.status === 'upcoming').length;
  const pastCount = bookings.filter((b) => b.status === 'completed' || b.status === 'cancelled').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">

          {/* Profile Hero */}
          <div className="relative bg-gradient-to-r from-rose-600 via-rose-500 to-orange-400 rounded-3xl overflow-hidden mb-8 shadow-xl">
            {/* Decorative blobs */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/4" />
            <div className="absolute bottom-0 left-1/3 w-40 h-40 bg-white/5 rounded-full translate-y-1/2" />
            <div className="relative z-10 p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm border-2 border-white/30 flex items-center justify-center text-white font-bold text-3xl shadow-lg flex-shrink-0">
                S
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                  Sarah Nakato
                </h1>
                <p className="text-rose-100 text-sm">Kampala, Uganda · Member since 2022</p>
                <div className="flex flex-wrap gap-4 mt-3">
                  <div className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full">
                    <span className="text-amber-300 text-sm">★</span>
                    <span className="text-white text-xs font-semibold">4.9 guest rating</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full">
                    <span className="text-white text-xs font-semibold">✈️ {bookings.filter(b => b.status === 'completed').length} trips completed</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full">
                    <span className="text-white text-xs font-semibold">❤️ 4 saved properties</span>
                  </div>
                </div>
              </div>
              <a href="/guest-dashboard" className="flex-shrink-0 px-5 py-2.5 bg-white text-[#4088C6] text-sm font-bold rounded-xl hover:bg-[#4088C6]/10 transition-colors shadow-sm">
                Full Dashboard
              </a>
            </div>
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: 'Active Reservations', value: activeCount.toString(), icon: '🏡', color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { label: 'Past Bookings', value: pastCount.toString(), icon: '📋', color: 'text-sky-600', bg: 'bg-sky-50' },
              { label: 'Wishlist Items', value: '4', icon: '❤️', color: 'text-rose-600', bg: 'bg-rose-50' },
              { label: 'Total Spent', value: 'UGX 7.8M', icon: '💳', color: 'text-violet-600', bg: 'bg-violet-50' },
            ].map((stat) => (
              <div key={stat.label} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
                <div className={`w-10 h-10 ${stat.bg} rounded-xl flex items-center justify-center text-lg mb-3`}>
                  {stat.icon}
                </div>
                <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
                <div className="text-xs text-gray-500 mt-0.5">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Tab nav */}
          <div className="flex gap-1 bg-white rounded-2xl p-1.5 shadow-sm border border-gray-100 mb-8 overflow-x-auto">
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveSection(s.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all duration-200 flex-1 justify-center ${
                  activeSection === s.id
                    ? 'bg-[#4088C6] text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <span>{s.icon}</span>
                <span>{s.label}</span>
              </button>
            ))}
          </div>

          {/* Section content */}
          <div>{renderSection()}</div>
        </div>
      </div>
    </div>
  );
}
